import { DataGrid, GridActionsCellItem, GridColDef, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import NotFilter from '../../../../../shared/components/NotFilter'
import useScreenWidth from '../../../../../shared/hooks/useScreenWidth'
import CourseLanguagesCell from './CourseLanguagesCell'
import CourseTagsCell from './CourseTagsCell'
import CourseTitleCell from './CourseTitleCell'
import CustomToolbar from '../../../../../shared/components/table/CustomToolbar'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ViewIcon from '@mui/icons-material/Visibility'
import { useNavigate } from 'react-router'

interface CourseTableProps {
  courses: any[]
  loading: boolean
  onEdit: (id: number) => void
  onDelete: (id: number) => void
  onHighlight: (id: number, isHighlight: boolean) => void
  onSelectionChange: (selectedIds: number[]) => void
  handleClickSetOptions: (options: any) => void
  globalBrandId: number | null
}

const CoursesTable = ({
  courses,
  loading,
  onEdit,
  onDelete,
  onHighlight,
  onSelectionChange,
  handleClickSetOptions,
  globalBrandId
}: CourseTableProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)
  const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 })
  const [selectedRows, setSelectedRows] = useState(0)

  const handleSelectionChange = (selectionModel: GridRowSelectionModel) => {
    onSelectionChange(selectionModel as number[])
    setSelectedRows(selectionModel.length)
  }

  const getColumns = (): GridColDef[] => {
    return [
      {
        field: 'title',
        headerName: t('name'),
        flex: 1,
        renderCell: (params: any) => <CourseTitleCell params={params.row} onHighlight={onHighlight} />
      },
      {
        field: 'tags',
        headerName: '',
        minWidth: 240,
        renderCell: (params: any) => <CourseTagsCell params={params} />
      },
      {
        field: 'brand',
        headerName: t('brand'),
        valueGetter: (params: any) => params.value?.name || '',
        hideable: globalBrandId !== null
      },
      {
        field: 'duration',
        headerName: t('duration'),
        hideable: isMobile
      },
      {
        field: 'language',
        headerName: t('language'),
        renderCell: (params: any) => <CourseLanguagesCell params={params} />,
        hideable: isMobile
      },
      {
        field: 'role',
        headerName: t('roles'),
        hideable: isMobile,
        maxWidth: 80
      },
      {
        field: 'actions',
        type: 'actions',
        sortable: false,
        filterable: false,
        maxWidth: 70,
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            key={params.id}
            icon={<ViewIcon />}
            label={t('view')}
            onClick={() => navigate(`${params.row.brand_id}/${params.row.slug}`)}
            showInMenu
          />,
          <GridActionsCellItem
            key={params.id}
            icon={<EditIcon />}
            label={t('edit')}
            onClick={() => {
              onEdit(Number(params.id))
            }}
            showInMenu
          />,
          <GridActionsCellItem
            key={params.id}
            icon={<DeleteIcon />}
            label={t('delete')}
            onClick={() => {
              onDelete(Number(params.id))
            }}
            showInMenu
          />
        ]
      }
    ].filter(column => !(isMobile && ['tags', 'duration', 'language', 'role'].includes(column.field)))
  }

  const columns = getColumns()

  if (courses.length === 0 && !loading) {
    return <NotFilter subsection={null} handleClearFilters={handleClickSetOptions} />
  }

  return (
    <DataGrid
      rows={courses}
      columns={columns}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={[10, 20, 30]}
      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
      autoHeight
      disableColumnMenu
      disableColumnFilter
      disableColumnSelector
      disableDensitySelector
      disableRowSelectionOnClick
      checkboxSelection
      onRowSelectionModelChange={handleSelectionChange}
      components={{ Toolbar: CustomToolbar }}
      slots={{ toolbar: CustomToolbar }}
      slotProps={{
        toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 }, selectedRows }
      }}
      density="comfortable"
      sx={{
        '& .MuiDataGrid-cell': { borderBottom: '1px solid #f0f0f0', whiteSpace: 'normal', wordBreak: 'break-word' },
        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#fafafa', borderBottom: 'none' },
        '& .MuiCheckbox-root.Mui-checked': { color: '#FC3E6F' }
      }}
    />
  )
}

export default CoursesTable
